/**
 * NCR Edit review actions (comments, approve/close, reject CAR, reopen, NCR
 * rejection decisions, CAR grading), split out of useNCREdit.
 */

import type { NavigateFunction } from 'react-router-dom';
import { qualityAPI, NonConformanceReport } from '../../lib/api';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../lib/api/types/common';
import { NCREditFormData } from './types';
import { customerOwns8D } from './ownership';

interface UseNCRReviewHandlersParams {
  /** NCR id from the route; the handlers no-op until it resolves. */
  id: string | undefined;
  ncr: NonConformanceReport | null;
  formData: NCREditFormData;
  newComment: string;
  setNewComment: (comment: string) => void;
  navigate: NavigateFunction;
  /** Refetch the NCR; called with `true` so the Tabs tree stays mounted. */
  fetchNCRDetails: (isRefresh?: boolean) => Promise<NonConformanceReport | undefined>;
  // Dialog open-state setters, closed on success.
  setCloseDialogOpen: (open: boolean) => void;
  setRejectCARDialogOpen: (open: boolean) => void;
  setReopenDialogOpen: (open: boolean) => void;
  setRejectNCRDialogOpen: (open: boolean) => void;
  // 8D scoring: the customer's CAR grades and their setters.
  rcScore: number | null;
  ercScore: number | null;
  caScore: number | null;
  epaScore: number | null;
  setRcScore: (value: number | null) => void;
  setErcScore: (value: number | null) => void;
  setCaScore: (value: number | null) => void;
  setEpaScore: (value: number | null) => void;
}

/**
 * Comment, approve/close, reject-CAR, reopen, NCR-rejection and CAR-grade
 * actions for the NCR edit page.
 *
 * @param params - route id, NCR and form state, dialog / score state and
 *   setters, navigate and the NCR refetch callback owned by useNCREdit
 * @returns handleAddComment, handleClose, handleApprove8D, handleRejectCAR, the four
 *   handle*ScoreChange handlers, handleReopen, handleRejectNCR,
 *   handleApproveRejection, handleDeclineRejection
 */
export function useNCRReviewHandlers({
  id,
  ncr,
  formData,
  newComment,
  setNewComment,
  navigate,
  fetchNCRDetails,
  setCloseDialogOpen,
  setRejectCARDialogOpen,
  setReopenDialogOpen,
  setRejectNCRDialogOpen,
  rcScore,
  ercScore,
  caScore,
  epaScore,
  setRcScore,
  setErcScore,
  setCaScore,
  setEpaScore,
}: UseNCRReviewHandlersParams) {
  const handleAddComment = async () => {
    if (!id || !newComment.trim()) return;

    try {
      await qualityAPI.addNCRComment(parseInt(id), newComment, false);
      setNewComment('');
      await fetchNCRDetails(true);
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const handleClose = async () => {
    if (!id) return;

    try {
      // "Close - No 8D Required" is offered from the form's 8D flag, which may
      // not be saved yet. The server closes an NCR before the CAR stage only
      // when its SAVED flag says no 8D is required, so persist the flag first.
      if (ncr && ncr.status !== 'car_submitted' && formData['is_8d_required'] === false
          && ncr.is_8d_required !== false) {
        await qualityAPI.updateNCR(parseInt(id), { is_8d_required: false });
      }
      // 8D scoring: send the customer's CAR grades with the approve decision.
      await qualityAPI.closeNCR(parseInt(id), {
        ...(rcScore != null ? { rc_score: rcScore } : {}),
        ...(ercScore != null ? { erc_score: ercScore } : {}),
        ...(caScore != null ? { ca_score: caScore } : {}),
        ...(epaScore != null ? { epa_score: epaScore } : {}),
      });
      toast.success(ncr?.status === 'car_submitted' ? '8D approved and NCR closed' : 'NCR closed');
      setCloseDialogOpen(false);
      await fetchNCRDetails(true);
    } catch (error: unknown) {
      // CR-6024 r2: surface the backend's actual error (permission, state
      // gate, missing collection/credit) so the user sees WHY Approve didn't
      // close the NCR. Previously this just said "Failed to close NCR" which
      // looked like the button was broken (Jacq rework).
      console.error('Error closing NCR:', error);
      // Round 4: getApiErrorMessage also reads the {message, details} envelope
      // the NCR save's own refusals use (the 8D-off step above).
      const err = error as { response?: { status?: number } };
      toast.error(err.response?.status === 403
        ? 'You do not have permission to close this NCR. If you are a Helix admin, use Login As to act as a customer user.'
        : err.response ? getApiErrorMessage(error, 'Failed to close NCR') : 'Failed to close NCR');
    }
  };

  // 24 Sep 2026: approve the CAR while other processes are still open. The
  // 8D is complete; the NCR stays open until the rest is done.
  const handleApprove8D = async () => {
    if (!id) return;
    try {
      await qualityAPI.approve8D(parseInt(id), {
        ...(rcScore != null ? { rc_score: rcScore } : {}),
        ...(ercScore != null ? { erc_score: ercScore } : {}),
        ...(caScore != null ? { ca_score: caScore } : {}),
        ...(epaScore != null ? { epa_score: epaScore } : {}),
      });
      const othersOpen = (ncr?.outstanding_processes ?? []).some((p) => p !== '8D');
      toast.success(othersOpen
        ? '8D approved. The NCR stays open until the other actions are complete.'
        : '8D approved. The NCR stays open until you close it.');
      setCloseDialogOpen(false);
      await fetchNCRDetails(true);
    } catch (error: unknown) {
      console.error('Error approving 8D:', error);
      const err = error as { response?: { status?: number; data?: { error?: string; detail?: string } } };
      toast.error(err.response?.data?.error || err.response?.data?.detail || 'Failed to approve the 8D');
    }
  };

  // CR-6024: customer rejects the submitted CAR and sends it back to the
  // supplier for rework. The reason becomes the banner the supplier sees
  // on the Corrective Action tab when the NCR drops back to ca_submitted.
  const handleRejectCAR = async (rejectionReason: string): Promise<boolean> => {
    if (!id) return false;
    const reason = rejectionReason.trim();
    if (!reason) {
      toast.error('Please provide a reason before sending the CAR back for rework.');
      return false;
    }
    try {
      // 8D scoring: send the customer's CAR grades with the rework decision.
      await qualityAPI.rejectCAR(parseInt(id), reason, {
        ...(rcScore != null ? { rc_score: rcScore } : {}),
        ...(ercScore != null ? { erc_score: ercScore } : {}),
        ...(caScore != null ? { ca_score: caScore } : {}),
        ...(epaScore != null ? { epa_score: epaScore } : {}),
      });
      // E27: a customer-owned CAR goes back to the customer's own team.
      toast.success(customerOwns8D(ncr) ? 'CAR sent back to your team for rework.' : 'CAR rejected. Supplier notified to rework.');
      setRejectCARDialogOpen(false);
      await fetchNCRDetails(true);
      return true;
    } catch (error: unknown) {
      // CR-6024 r2: same surfacing as handleClose — the previous generic toast
      // hid the underlying permission/state-gate failure (Jacq rework).
      console.error('Error rejecting CAR:', error);
      const err = error as { response?: { status?: number; data?: { error?: string; detail?: string } } };
      const backendMsg = err.response?.data?.error || err.response?.data?.detail;
      const msg = backendMsg
        || (err.response?.status === 403
              ? 'You do not have permission to reject this CAR. If you are a Helix admin, use Login As to act as a customer user.'
              : 'Failed to reject CAR');
      toast.error(msg);
      return false;
    }
  };

  // Persist a single grade the moment it's picked — independent of the
  // approve/reject decision — so the register column and 8D Score
  // (= ORC+ERC+OPA+EPA) update immediately. Previously the scores only saved as a side-effect of
  // close/reject, so a customer who graded but did neither lost them (the
  // "entered but not showing / not calculating" bug).
  const SCORE_SETTERS: Record<string, (v: number) => void> = {
    rc_score: setRcScore,
    erc_score: setErcScore,
    ca_score: setCaScore,
    epa_score: setEpaScore,
  };
  const persistCarScore = async (
    field: 'rc_score' | 'erc_score' | 'ca_score' | 'epa_score',
    value: number,
  ) => {
    SCORE_SETTERS[field](value);
    if (!id) return;
    try {
      await qualityAPI.setCarScores(parseInt(id), { [field]: value });
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to save score');
    }
  };
  const handleRcScoreChange = (value: number) => persistCarScore('rc_score', value);
  const handleErcScoreChange = (value: number) => persistCarScore('erc_score', value);
  const handleCaScoreChange = (value: number) => persistCarScore('ca_score', value);
  const handleEpaScoreChange = (value: number) => persistCarScore('epa_score', value);

  // CR-6024 r3: customer reopens a closed NCR. Spec acceptance criterion
  // "Closed NCRs can be reopened by customer users if required." Backend
  // /reopen_ncr/ already exists and transitions closed -> ca_submitted.
  const handleReopen = async () => {
    if (!id) return;
    try {
      await qualityAPI.reopenNCR(parseInt(id));
      toast.success('NCR reopened');
      setReopenDialogOpen(false);
      await fetchNCRDetails(true);
    } catch (error: unknown) {
      console.error('Error reopening NCR:', error);
      const err = error as { response?: { status?: number; data?: { error?: string; detail?: string } } };
      const backendMsg = err.response?.data?.error || err.response?.data?.detail;
      const msg = backendMsg
        || (err.response?.status === 403
              ? 'You do not have permission to reopen this NCR. If you are a Helix admin, use Login As to act as a customer user.'
              : 'Failed to reopen NCR');
      toast.error(msg);
    }
  };

  // Supplier: request rejection of the submitted NCR with a free-text reason.
  // Returns false so the dialog stays open on failure.
  const handleRejectNCR = async (reason: string): Promise<boolean> => {
    if (!id) return false;
    try {
      await qualityAPI.rejectNCR(parseInt(id), reason);
      toast.success('NCR rejection requested — awaiting customer decision');
      setRejectNCRDialogOpen(false);
      await fetchNCRDetails(true);
      return true;
    } catch (error: unknown) {
      console.error('Error rejecting NCR:', error);
      const err = error as { response?: { data?: { error?: string; detail?: string } } };
      toast.error(err.response?.data?.error || err.response?.data?.detail || 'Failed to reject NCR');
      return false;
    }
  };

  // Customer: approve the supplier's rejection — the backend voids the NCR
  // (status → deleted) and writes the audit trail, so navigate back to the list.
  const handleApproveRejection = async () => {
    if (!id) return;
    try {
      await qualityAPI.approveRejection(parseInt(id));
      toast.success('NCR rejection approved — NCR voided');
      navigate('/quality');
    } catch (error: unknown) {
      console.error('Error approving NCR rejection:', error);
      const err = error as { response?: { data?: { error?: string; detail?: string } } };
      toast.error(err.response?.data?.error || err.response?.data?.detail || 'Failed to approve rejection');
    }
  };

  // Customer: decline the supplier's rejection — the NCR continues unchanged.
  const handleDeclineRejection = async () => {
    if (!id) return;
    try {
      await qualityAPI.declineRejection(parseInt(id));
      toast.success('NCR rejection declined');
      await fetchNCRDetails(true);
    } catch (error: unknown) {
      console.error('Error declining NCR rejection:', error);
      const err = error as { response?: { data?: { error?: string; detail?: string } } };
      toast.error(err.response?.data?.error || err.response?.data?.detail || 'Failed to decline rejection');
    }
  };

  return {
    handleAddComment,
    handleClose,
    handleApprove8D,
    handleRejectCAR,
    handleRcScoreChange,
    handleErcScoreChange,
    handleCaScoreChange,
    handleEpaScoreChange,
    handleReopen,
    handleRejectNCR,
    handleApproveRejection,
    handleDeclineRejection,
  };
}
